import React, { useEffect, useState } from "react";
import axios from "axios";
import { ExternalLink, RefreshCw, FileSpreadsheet } from "lucide-react";
import GlassCard from "../../components/ui-custom/GlassCard";
import Reveal from "../../components/ui-custom/Reveal";
import CellValue from "../../components/ui-custom/CellValue";
import { Button } from "../../components/ui/button";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

const MiniTable = ({ title, columns, rows }) => (
  <GlassCard className="overflow-hidden">
    <div className="px-6 py-4 border-b border-border/10">
      <h3 className="font-display font-semibold text-foreground">{title}</h3>
    </div>
    {rows.length === 0 ? (
      <p className="text-sm text-muted-foreground px-6 py-8 text-center">No rows added yet.</p>
    ) : (
      <div className="overflow-x-auto">
        <table className="w-full min-w-[640px] sm:min-w-0 sm:table-fixed text-sm">
          <thead>
            <tr className="text-left text-muted-foreground uppercase text-xs tracking-wide border-b border-border/10">
              {columns.map((c) => (
                <th key={c} className="px-6 py-3 whitespace-nowrap sm:whitespace-normal sm:break-words">
                  {c}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={i} className="border-b border-border/5">
                {columns.map((c) => (
                  <td
                    key={c}
                    className="px-6 py-3 text-muted-foreground align-top whitespace-nowrap sm:whitespace-normal sm:break-words"
                  >
                    <CellValue value={row[c]} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </GlassCard>
);

const MySheet = () => {
  const [sheetUrl, setSheetUrl] = useState(null);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [candidate, setCandidate] = useState(null);
  const [linking, setLinking] = useState(false);

  const load = () => {
    setLoading(true);
    setError("");
    setCandidate(null);
    // Sequential, not parallel: /my-sheet resolves (and persists) which
    // sheet is theirs, which /my-data would otherwise redundantly redo -
    // running both at once was doubling the Drive lookup work and tripping
    // the request timeout.
    axios
      .get(`${API}/employees/my-sheet`, { withCredentials: true, timeout: 40000 })
      .then((sheetRes) => {
        if (sheetRes.data.needs_confirmation) {
          setCandidate(sheetRes.data.candidate);
          return null;
        }
        setSheetUrl(sheetRes.data.url);
        return axios.get(`${API}/employees/my-data`, { withCredentials: true, timeout: 40000 });
      })
      .then((dataRes) => {
        if (dataRes) setData(dataRes.data);
      })
      .catch((err) => {
        if (err.response?.status === 400) {
          setError("Complete your profile (with your Employee Code) first, then come back here.");
        } else if (err.code === "ECONNABORTED") {
          setError("This is taking too long. Please try again in a moment.");
        } else if (err.response?.status === 502) {
          // TEMPORARY DEBUG: shows the real backend error while we track
          // down why sheet resolution is failing in production.
          setError(`Could not load your sheet: ${err.response.data?.detail}`);
        } else {
          setError("Could not load your sheet right now. Please try again in a moment.");
        }
      })
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const linkCandidate = () => {
    if (!candidate) return;
    setLinking(true);
    axios
      .post(`${API}/employees/my-sheet/link`, { sheet_id: candidate.id }, { withCredentials: true })
      .then(() => {
        setCandidate(null);
        load();
      })
      .catch(() => setError("Could not link that sheet. Please try again in a moment."))
      .finally(() => setLinking(false));
  };

  const createNewInstead = () => {
    setLinking(true);
    axios
      .post(`${API}/employees/my-sheet/create-new`, {}, { withCredentials: true })
      .then(() => {
        setCandidate(null);
        load();
      })
      .catch(() => setError("Could not create a new sheet. Please try again in a moment."))
      .finally(() => setLinking(false));
  };

  const resetAndSearchAgain = () => {
    setLinking(true);
    axios
      .post(`${API}/employees/my-sheet/reset`, {}, { withCredentials: true })
      .then(() => {
        setSheetUrl(null);
        setData(null);
        load();
      })
      .catch(() => setError("Could not reset your sheet link. Please try again in a moment."))
      .finally(() => setLinking(false));
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="font-display font-semibold text-2xl text-foreground mb-1">My Sheet</h2>
          <p className="text-sm text-muted-foreground">
            Add or edit your own deliverables and order bookings directly in your personal Google Sheet.
          </p>
        </div>
        <div className="flex gap-2">
          {sheetUrl && (
            <a href={sheetUrl} target="_blank" rel="noopener noreferrer">
              <Button className="btn-glow-primary gap-2">
                <FileSpreadsheet size={16} /> Open My Sheet <ExternalLink size={14} />
              </Button>
            </a>
          )}
          <Button variant="outline" className="gap-2" onClick={load} disabled={loading}>
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
          </Button>
        </div>
      </div>

      {loading && !data && !candidate && <p className="text-sm text-muted-foreground">Setting up your sheet…</p>}

      {candidate && (
        <Reveal>
          <GlassCard className="p-8 text-center max-w-xl mx-auto">
            <FileSpreadsheet size={28} className="mx-auto mb-3 text-qcyan" />
            <p className="text-foreground font-medium mb-1">
              We found a file that matches your employee code:
            </p>
            <p className="text-sm text-muted-foreground mb-6">"{candidate.name}"</p>
            <p className="text-sm text-muted-foreground mb-6">
              Is this your sheet? If yes, we'll set it up with the right tabs and use it from now on.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <Button className="btn-glow-primary gap-2" onClick={linkCandidate} disabled={linking}>
                Yes, use this sheet
              </Button>
              <Button variant="outline" onClick={createNewInstead} disabled={linking}>
                No, create a new one
              </Button>
            </div>
          </GlassCard>
        </Reveal>
      )}

      {error && (
        <Reveal>
          <GlassCard className="p-8 text-center">
            <p className="text-foreground font-medium">{error}</p>
          </GlassCard>
        </Reveal>
      )}

      {data && (
        <div className="space-y-6">
          <p className="text-xs text-muted-foreground">
            Edits made in Google Sheets can take up to 2 minutes to appear here - tap Refresh after editing.{" "}
            <button
              onClick={resetAndSearchAgain}
              disabled={linking}
              className="underline underline-offset-2 hover:text-foreground disabled:opacity-50"
            >
              Not the right sheet? Reset and search again.
            </button>
          </p>
          <Reveal>
            <MiniTable
              title={data.deliverables.tab_name || "Deliverables"}
              columns={data.deliverables.columns}
              rows={data.deliverables.rows}
            />
          </Reveal>
          <Reveal delay={0.06}>
            <MiniTable
              title={data.order_bookings.tab_name || "Order Bookings"}
              columns={data.order_bookings.columns}
              rows={data.order_bookings.rows}
            />
          </Reveal>
        </div>
      )}
    </div>
  );
};

export default MySheet;
